import {
  SpeedTestArrowPicker,
  SPEED_TEST_ARROW_TYPES,
} from '@pulsoid-private/widgets-lib';
import React, {useEffect, useState} from 'react';
import {Col, Form, Row} from 'react-bootstrap';
import {Action} from '../../../system/buttons';
import {OffsetRanges} from '../../../system/OffsetRanges/OffsetRanges';
import {PremiumWrapperOverlay} from '../../PremiumWrapperOverlay';
import ColorPicker from './ColorPicker';
import merge from 'lodash/merge';
import {NewFontManager} from '../../../system/NewFontManager/NewFontManager';

const DEFAULT_CONFIG = {
  max: 180,
  min: 60,
  bpmText: {
    color: '#ffffff',
    font: {
      name: 'Open Sans',
      url: null,
    },
  },
  additionalText: {
    show: true,
    value: 'PULSOID',
    color: '#ffffff',
  },
  arrow: {
    show: true,
    color: '#ffffff',
    type: SPEED_TEST_ARROW_TYPES.SPEEDTEST,
  },
  arc: {
    gradientRanges: [
      {color: '#00b7fe', offset: 0},
      {color: '#7cff74', offset: 100},
    ],
    strokeWidth: 40,
    backgroundColor: '#223050',
  },
  marksOptions: {
    color: '#ffffff',
    withLines: false,
    withNumbers: true,
    quantity: 7,
  },
};

export const SpeedTestEdit = (props) => {
  const [configuration, setConfiguration] = useState(
    merge({}, DEFAULT_CONFIG, props.configuration)
  );

  const updateConfiguration = (configPart: Partial<typeof DEFAULT_CONFIG>) => {
    const newConfiguration = {...configuration, ...configPart};
    setConfiguration(newConfiguration);
    props.onUpdate(newConfiguration);
  };

  const handleOnSave = () => {
    props.onSave(configuration);
  };

  useEffect(() => {
    props.onUpdate(configuration);
  }, []);

  return (
    <>
      <PremiumWrapperOverlay
        bi={{
          from: 'widget_edit_premium_update',
          id: props.id,
        }}
        disabled={!props.premiumUpdate}
      >
        <h4>BPM Configuration</h4>
        <Form.Group>
          <Row>
            <Form.Label column sm="2" className="d-flex align-items-center">
              Text
            </Form.Label>

            <Form.Group as={Col} sm="2" className="p-0">
              <Form.Label column>Color</Form.Label>
              <Col>
                <ColorPicker
                  color={configuration.bpmText?.color}
                  onChangeComplete={(color) =>
                    updateConfiguration({
                      bpmText: {...configuration.bpmText, color},
                    })
                  }
                />
              </Col>
            </Form.Group>

            <NewFontManager
              as={Col}
              onChange={(font) =>
                updateConfiguration({
                  bpmText: {...configuration.bpmText, font},
                })
              }
              font={configuration?.bpmText?.font}
              withUpload
            />
          </Row>
        </Form.Group>

        <h4>Dial Configuration</h4>

        <Form.Group>
          <Row>
            <Form.Label column sm="2" className="d-flex align-items-center">
              Label
            </Form.Label>

            <Form.Group as={Col} sm="2" className="p-0 ">
              <Form.Label column htmlFor="configuration.additionalText.show">
                Show
              </Form.Label>
              <Col>
                <Form.Switch
                  id="configuration.additionalText.show"
                  defaultChecked={configuration.additionalText?.show}
                  onChange={(evt) =>
                    updateConfiguration({
                      additionalText: {
                        ...configuration.additionalText,
                        show: (evt?.target as HTMLInputElement)?.checked,
                      },
                    })
                  }
                />
              </Col>
            </Form.Group>

            <Form.Group as={Col} sm="2" className="p-0 ">
              <Form.Label column htmlFor="configuration.additionalText.value">
                Text
              </Form.Label>
              <Col>
                <Form.Control
                  id="configuration.additionalText.value"
                  type="text"
                  value={configuration.additionalText?.value}
                  onChange={(evt) =>
                    updateConfiguration({
                      additionalText: {
                        ...configuration.additionalText,
                        value: evt?.target?.value,
                      },
                    })
                  }
                />
              </Col>
            </Form.Group>

            <Form.Group as={Col} sm="2" className="p-0 ">
              <Form.Label column htmlFor="configuration.additionalText.color">
                Color
              </Form.Label>
              <Col>
                <ColorPicker
                  color={configuration.additionalText?.color}
                  onChangeComplete={(color) =>
                    updateConfiguration({
                      additionalText: {
                        ...configuration.additionalText,
                        color,
                      },
                    })
                  }
                />
              </Col>
            </Form.Group>
          </Row>

          <Row>
            <Form.Label column sm="2" className="d-flex align-items-center">
              Values
            </Form.Label>
            <Form.Group as={Col} sm="2" className="p-0 ">
              <Form.Label column htmlFor="configuration.min">
                Min
              </Form.Label>
              <Col>
                <Form.Control
                  id="configuration.min"
                  type="number"
                  value={configuration.min}
                  max={configuration.max}
                  onChange={(evt) =>
                    updateConfiguration({min: Number(evt?.target?.value)})
                  }
                />
              </Col>
            </Form.Group>

            <Form.Group as={Col} sm="2" className="p-0 ">
              <Form.Label column htmlFor="configuration.max">
                Max
              </Form.Label>
              <Col>
                <Form.Control
                  id="configuration.max"
                  type="number"
                  value={configuration.max}
                  min={configuration.min}
                  onChange={(evt) =>
                    updateConfiguration({max: Number(evt?.target?.value)})
                  }
                />
              </Col>
            </Form.Group>
          </Row>

          <Row>
            <Form.Label column sm="2" className="d-flex align-items-center">
              Marks
            </Form.Label>

            <Form.Group as={Col} sm="1" className="p-0 ">
              <Form.Label column htmlFor="markOptions.withNumbers">
                Numbers
              </Form.Label>
              <Col>
                <Form.Switch
                  id="markOptions.withNumbers"
                  defaultChecked={configuration.marksOptions?.withNumbers}
                  onChange={(evt) =>
                    updateConfiguration({
                      marksOptions: {
                        ...configuration.marksOptions,
                        withNumbers: (evt?.target as HTMLInputElement)?.checked,
                      },
                    })
                  }
                />
              </Col>
            </Form.Group>

            <Form.Group as={Col} sm="1" className="p-0 ">
              <Form.Label column htmlFor="markOptions.withLines">
                Lines
              </Form.Label>
              <Col>
                <Form.Switch
                  id="markOptions.withLines"
                  defaultChecked={configuration.marksOptions?.withLines}
                  onChange={(evt) =>
                    updateConfiguration({
                      marksOptions: {
                        ...configuration.marksOptions,
                        withLines: (evt?.target as HTMLInputElement)?.checked,
                      },
                    })
                  }
                />
              </Col>
            </Form.Group>

            {configuration.marksOptions?.withNumbers ||
            configuration.marksOptions?.withLines ? (
              <>
                <Form.Group as={Col} sm="2" className="p-0 ">
                  <Form.Label column>Quantity</Form.Label>
                  <Col>
                    <Form.Control
                      type="number"
                      value={configuration.marksOptions?.quantity}
                      min={2}
                      onChange={(evt) =>
                        updateConfiguration({
                          marksOptions: {
                            ...configuration.marksOptions,
                            quantity: Number(evt?.target?.value),
                          },
                        })
                      }
                    />
                  </Col>
                </Form.Group>

                <Form.Group as={Col} sm="2" className="p-0 ">
                  <Form.Label column htmlFor="markOptions.color">
                    Marks Color
                  </Form.Label>
                  <Col>
                    <ColorPicker
                      color={configuration.marksOptions?.color}
                      onChangeComplete={(color) =>
                        updateConfiguration({
                          marksOptions: {
                            ...configuration.marksOptions,
                            color,
                          },
                        })
                      }
                    />
                  </Col>
                </Form.Group>
              </>
            ) : null}
          </Row>

          <Row>
            <Form.Label column sm="2" className="d-flex align-items-center">
              Pointer Type
            </Form.Label>

            <Row>
              <Form.Group as={Col}>
                <Col>
                  <SpeedTestArrowPicker
                    selectedItem={configuration?.arrow?.type}
                    onSelect={(type) =>
                      updateConfiguration({
                        arrow: {
                          ...configuration.arrow,
                          show: type !== null,
                          type: type,
                        },
                      })
                    }
                  />
                </Col>
              </Form.Group>
            </Row>
          </Row>

          <Row>
            <Form.Label column sm="2" className="d-flex align-items-center">
              Arc Style
            </Form.Label>
            <Form.Group as={Col} sm="2" className="p-0 ">
              <Form.Label column>Background Color</Form.Label>
              <Col>
                <ColorPicker
                  color={configuration.arc?.backgroundColor}
                  onChangeComplete={(color) =>
                    updateConfiguration({
                      arc: {...configuration.arc, backgroundColor: color},
                    })
                  }
                />
              </Col>
            </Form.Group>

            <Form.Group as={Col} sm="2" className="p-0 ">
              <Form.Label column>Width</Form.Label>
              <Col>
                <Form.Control
                  type="range"
                  value={configuration.arc?.strokeWidth}
                  min={1}
                  max={80}
                  onChange={(evt) =>
                    updateConfiguration({
                      arc: {
                        ...configuration.arc,
                        strokeWidth: Number(evt?.target?.value),
                      },
                    })
                  }
                />
              </Col>
            </Form.Group>

            <Form.Group as={Col} sm="2" className="p-0 ">
              <Form.Label column>Shadow</Form.Label>
              <Col>
                <Form.Switch
                  id="arc.showShadow"
                  defaultChecked={configuration.arc?.showShadow}
                  onChange={(evt) =>
                    updateConfiguration({
                      arc: {
                        ...configuration.arc,
                        showShadow: (evt?.target as HTMLInputElement)?.checked,
                      },
                    })
                  }
                />
              </Col>
            </Form.Group>
          </Row>
        </Form.Group>

        <h4>Arc Gradient</h4>
        <Form.Group>
          <OffsetRanges
            ranges={configuration.arc.gradientRanges.map((range) => ({
              offset: range.offset,
              value: range.color,
            }))}
            onChange={(ranges) =>
              updateConfiguration({
                arc: {
                  ...configuration.arc,
                  gradientRanges: ranges.map((range) => ({
                    offset: range.offset,
                    color: String(range.value),
                  })),
                },
              })
            }
            defaultRange={{
              offset: 0,
              value: '#00b7fe',
            }}
            controlTitle="Color"
            control={OffsetRangeColorControl}
          />
        </Form.Group>
      </PremiumWrapperOverlay>

      <Action onClick={handleOnSave}>Save settings</Action>
    </>
  );
};

const OffsetRangeColorControl = ({value, onChange}) => (
  <ColorPicker
    color={value}
    onChangeComplete={(color: string) => onChange(color)}
  />
);
